import { defineStore } from 'pinia';
import { useOrgStore } from '@/stores/org';

/**
 * Per-user, per-grid presentation preferences (resized column widths today,
 * extensible to other per-grid settings later).
 *
 * Personal to the signed-in user and persisted server-side (org connection), so
 * a user's column widths follow them across devices. Distinct from the
 * organization-wide column configuration in `listConfig` — that is an admin
 * default; this is a personal override.
 *
 * Every grid's preferences are fetched in ONE batch request the first time any
 * grid mounts, then served from cache for the rest of the session — so a page
 * with several grids costs a single request, not one per grid. Keyed by a grid's
 * `resource` slug (e.g. 'contacts', 'tasks'), matching the key every grid already
 * uses for its localStorage filter/panel state.
 */
export const useGridPreferencesStore = defineStore('gridPreferences', {
    state: () => ({
        prefs: {},
        loadedAll: false,
        loadingPromise: null,
        saveTimers: {}
    }),

    getters: {
        getPreferences: state => gridKey => state.prefs[gridKey] || {},
        getColumnWidths: state => gridKey => state.prefs[gridKey]?.column_widths || {},
        // The user's per-grid column visibility/order override (empty = follow the
        // organization's list configuration). Shape: [{ field, visible }] in order.
        getColumnConfig: state => gridKey => state.prefs[gridKey]?.columns || [],
        // This user's layout for one list — 'adaptive', 'narrow' or 'full'. Empty
        // means the organization's choice, which is not one of the three: a list
        // left alone has to keep following Settings ▸ Lists when it changes.
        getLayoutMode: state => gridKey => state.prefs[gridKey]?.layout_mode || '',
        // How a record opened from one list opens — 'read', 'edit', or empty for
        // the organization's preference, which is a third answer and not either of
        // them.
        getOpenMode: state => gridKey => state.prefs[gridKey]?.open_mode || '',
        // How this user reads activity feeds: true for newest at the top, false
        // for the conversation order (newest at the bottom, by the composer).
        feedNewestFirst: state => state.prefs.activities?.feed_order === 'newest_first'
    },

    actions: {
        /**
         * Fetch every grid's preferences for the current user once. Concurrent
         * callers (multiple grids mounting together) share the one in-flight
         * request.
         */
        loadAll() {
            if (this.loadedAll) {
                return Promise.resolve(this.prefs);
            }
            if (this.loadingPromise) {
                return this.loadingPromise;
            }
            const orgStore = useOrgStore();
            const api = orgStore.api;
            this.loadingPromise = axios
                .get(`${api}/grid-preferences`)
                .then(({ data }) => {
                    // Answered for an organization since left: not this one's preferences.
                    if (orgStore.api !== api) return;
                    this.prefs = data && typeof data === 'object' ? data : {};
                    this.loadedAll = true;
                })
                .catch(() => {
                    // Non-critical: grids fall back to their default column widths.
                    this.loadedAll = true;
                })
                .finally(() => {
                    this.loadingPromise = null;
                });
            return this.loadingPromise;
        },

        /**
         * Take an answer already on its way — the list bootstrap's. It stands in
         * for the shared in-flight request `loadAll()` returns; a part left out
         * (null) falls back to the endpoint.
         *
         * @param {Promise<Object|null>} pending
         */
        prime(pending) {
            if (this.loadedAll || this.loadingPromise) {
                return;
            }

            this.loadingPromise = pending
                .then(data => {
                    if (data == null) {
                        throw new Error('grid preferences left out of the bootstrap');
                    }
                    this.prefs = typeof data === 'object' ? data : {};
                    this.loadedAll = true;
                    this.loadingPromise = null;
                })
                .catch(() => {
                    this.loadingPromise = null;

                    return this.loadAll();
                });
        },

        /**
         * Forget everything read for the organization being left. A save still
         * waiting on its debounce is sent first — `flushSave` reads the API base
         * and the preferences before its first await, so it still reaches the
         * organization the change was made in.
         */
        reset() {
            for (const gridKey of Object.keys(this.saveTimers)) {
                clearTimeout(this.saveTimers[gridKey]);
                delete this.saveTimers[gridKey];
                this.flushSave(gridKey);
            }

            this.prefs = {};
            this.loadedAll = false;
            this.loadingPromise = null;
        },

        async loadPreferences(gridKey) {
            await this.loadAll();
            return this.prefs[gridKey] || {};
        },

        /**
         * Persist the full set of column widths for a grid (field => px).
         */
        saveColumnWidths(gridKey, widths) {
            this.savePreference(gridKey, 'column_widths', { ...widths });
        },

        /**
         * Persist the user's column visibility/order override for a grid. Debounced
         * so toggling several columns in a row coalesces into one request.
         * `columns` is an ordered array of { field, visible }.
         */
        saveColumnConfig(gridKey, columns) {
            this.savePreference(
                gridKey,
                'columns',
                columns.map(c => ({ field: c.field, visible: !!c.visible }))
            );
        },

        /**
         * Persist this user's layout for one list. An empty mode is stored as null —
         * "follow the organization", so a later change in Settings ▸ Lists reaches
         * this list too.
         */
        saveLayoutMode(gridKey, mode) {
            this.savePreference(gridKey, 'layout_mode', mode || null);
        },

        /**
         * Persist how records opened from one list open for this user. An empty
         * mode is stored as null — "follow the organization", so a later change in
         * Settings ▸ Objects reaches this list too.
         */
        saveOpenMode(gridKey, mode) {
            this.savePreference(gridKey, 'open_mode', mode || null);
        },

        /**
         * Persist how this user reads every activity feed. Not newest-first is
         * stored as null — the conversation order, which is the default.
         */
        saveFeedNewestFirst(newestFirst) {
            this.savePreference('activities', 'feed_order', newestFirst ? 'newest_first' : null);
        },

        /**
         * Drop the user's column override so the grid follows the organization's
         * list configuration again. Persisted immediately.
         */
        resetColumns(gridKey) {
            if (!gridKey) {
                return;
            }
            this.prefs[gridKey] = { ...(this.prefs[gridKey] || {}), columns: [] };
            if (this.saveTimers[gridKey]) {
                clearTimeout(this.saveTimers[gridKey]);
                delete this.saveTimers[gridKey];
            }
            this.flushSave(gridKey);
        },

        /**
         * Clear a grid's saved column widths so it returns to its default layout.
         * Persisted immediately (a deliberate user action, not a drag burst).
         */
        resetColumnWidths(gridKey) {
            if (!gridKey) {
                return;
            }
            this.prefs[gridKey] = { ...(this.prefs[gridKey] || {}), column_widths: {} };
            if (this.saveTimers[gridKey]) {
                clearTimeout(this.saveTimers[gridKey]);
                delete this.saveTimers[gridKey];
            }
            this.flushSave(gridKey);
        },

        /**
         * Whether this user has arranged a list their own way — columns, widths or
         * layout. That is what a reset of the list loses for good, so it is what
         * asking first is about.
         *
         * @param {string[]} gridKeys The list's keys (its object type and its resource)
         */
        hasPersonalDisplay(gridKeys) {
            return gridKeys.filter(Boolean).some(gridKey => {
                const prefs = this.prefs[gridKey] || {};

                return (
                    (prefs.columns || []).length > 0 ||
                    Object.keys(prefs.column_widths || {}).length > 0 ||
                    !!prefs.layout_mode
                );
            });
        },

        /**
         * Put a list's display back to the organization's configuration: columns,
         * widths and layout. Columns and layout are kept under the list's object
         * type, widths under its resource, so both keys are given. One save per
         * key, sent at once (a deliberate action, not a drag burst).
         *
         * @param {string[]} gridKeys
         */
        resetListDisplay(gridKeys) {
            for (const gridKey of new Set(gridKeys.filter(Boolean))) {
                this.prefs[gridKey] = {
                    ...(this.prefs[gridKey] || {}),
                    columns: [],
                    column_widths: {},
                    layout_mode: null
                };
                if (this.saveTimers[gridKey]) {
                    clearTimeout(this.saveTimers[gridKey]);
                    delete this.saveTimers[gridKey];
                }
                this.flushSave(gridKey);
            }
        },

        /**
         * Merge one preference key for a grid and persist it (debounced). Generic
         * so future per-grid user settings reuse the same path.
         */
        savePreference(gridKey, key, value) {
            if (!gridKey) {
                return;
            }
            this.prefs[gridKey] = { ...(this.prefs[gridKey] || {}), [key]: value };
            this.queueSave(gridKey);
        },

        queueSave(gridKey) {
            if (this.saveTimers[gridKey]) {
                clearTimeout(this.saveTimers[gridKey]);
            }
            this.saveTimers[gridKey] = setTimeout(() => {
                delete this.saveTimers[gridKey];
                this.flushSave(gridKey);
            }, 400);
        },

        async flushSave(gridKey) {
            try {
                const orgStore = useOrgStore();
                // Send the grid's full preferences (the local source of truth), so a
                // burst of edits to different keys never drops one another.
                await axios.put(`${orgStore.api}/grid-preferences/${gridKey}`, {
                    preferences: this.prefs[gridKey] || {}
                });
            } catch {
                // Best-effort: a failed save just means the widths aren't kept.
            }
        }
    }
});
